import { GapNote } from '../../ui/Gaps'
import { formatShortSum, type TopRow } from './model'

export interface TopPanelProps {
  title: string
  rows: TopRow[]
  empty: string
  /** Как показать число: деньги или просто счёт. */
  unit?: 'money' | 'count'
  /** Строки панели ведут в карточку: у аптек и товаров это разные разделы. */
  opens?: string
}

function value(row: TopRow, unit: TopPanelProps['unit']): string {
  return unit === 'money' ? formatShortSum(row.value) : String(row.value)
}

/**
 * Панель «топа»: полоса — доля от наибольшего значения, поэтому полосы
 * сравнимы. Сервер строки не сортирует, поэтому порядок и максимум считаем
 * здесь: с первой строкой как максимумом «1» и «6» выглядели одинаково полными.
 *
 * Строки не кликабельны, и это помечено: в ответе дашборда у точки есть
 * только подпись, без идентификатора товара, категории или аптеки, — вести по
 * названию значило бы угадывать (docs/open-questions.md).
 */
export function TopPanel({ title, rows, empty, unit = 'count', opens }: TopPanelProps) {
  const sorted = [...rows].sort((a, b) => b.value - a.value)
  // Не меньше единицы: одни нули дают пустые полосы, а не деление на ноль.
  const max = Math.max(...sorted.map((row) => row.value), 1)

  return (
    <section className="ui-card">
      <div className="ui-card-head">
        <h3>{title}</h3>
      </div>
      <div className="ui-card-body">
        {sorted.length === 0 ? <p className="dsh-empty">{empty}</p> : null}
        {sorted.length ? (
          <ol className="dsh-top">
            {sorted.map((row) => (
              <li key={row.name}>
                <span className="name">
                  {row.name}
                  {row.note ? <span className="note"> · {row.note}</span> : null}
                </span>
                <span className="bar" aria-hidden="true">
                  <i style={{ width: `${Math.round((row.value / max) * 100)}%` }} />
                </span>
                <span className="value">{value(row, unit)}</span>
              </li>
            ))}
          </ol>
        ) : null}
        {opens && sorted.length ? (
          <GapNote what={`Переход в ${opens}`} where="GET /admin/dashboard">
            Строки не открываются: в ответе дашборда у точки есть только подпись, без идентификатора
            — вести в {opens} по названию значило бы угадывать.
          </GapNote>
        ) : null}
      </div>
    </section>
  )
}
